/**
 * « Mes cours », côté élève.
 *
 * Distinct de `MyCoursesPage` du catalogue, qui liste les cours qu'un enseignant
 * **écrit** : ce sont deux questions différentes posées au même mot, et les
 * confondre donnerait un écran qui mélange un brouillon en cours de rédaction et
 * un cours qu'on suit.
 */
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { useMyEnrollments } from "@/features/learning/api/useLearning";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

export function MyLearningPage() {
  const { t } = useTranslation("learning");
  const enrollments = useMyEnrollments();

  if (enrollments.isPending) {
    return <Skeleton className="mx-auto my-8 h-48 w-full max-w-4xl" label={t("mine.title")} />;
  }
  if (enrollments.isError) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <ErrorState error={enrollments.error} />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{t("mine.title")}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t("mine.subtitle")}</p>
      </header>

      {enrollments.data.length === 0 ? (
        <Card title={t("mine.title")}>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("mine.empty")}</p>
        </Card>
      ) : (
        <ul className="space-y-3">
          {enrollments.data.map((enrollment) => (
            <li key={enrollment.id}>
              <Card title={enrollment.course_title}>
                <div className="space-y-2">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {t("player.progress", {
                      done: enrollment.completed_lesson_count,
                      total: enrollment.lesson_count,
                      percent: enrollment.progress_pct,
                    })}
                  </p>
                  <Link
                    to="/learn/$slug"
                    params={{ slug: enrollment.course_slug }}
                    className="text-brand-600 text-sm hover:underline"
                  >
                    {enrollment.completed_at === null ? t("mine.resume") : t("mine.review")}
                  </Link>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
